import { Button, Form, InputGroup, Modal, Spinner } from 'react-bootstrap'
import { Tooltip } from '@mui/material'
import ErrorIcon from '@mui/icons-material/Error'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useStore } from '../../../app/stores/store'
import SolvencyRenderer from './SolvencyRenderer'
import AttachmentReviewModal from './AttachmentReviewModal'
import { AuctionCardLine } from '../Lines/AuctionCardLine'

function AuctionSolvencyAttachment() {
  const { bidsStore, userStore, auctionDetailsStore, methodsStore } = useStore()
  const { auctionDetails } = bidsStore
  const [showAttachment, setShowAttachment] = useState(true)
  const [showReview, setShowReview] = useState(false)
  const { id } = useParams()
  const [attachment, setAttachment] = useState({
    file: null,
    auctionId: id!,
  })
  const { t } = useTranslation()
  const [loadingBtn, setLoadingBtn] = useState(0)

  useEffect(() => {
    const currentTime = new Date()
    const deadline = new Date(auctionDetails?.deadline || '')
    const deadlineMinus24Hours = new Date(deadline.getTime() - 24 * 60 * 60 * 1000)

    if (currentTime > deadlineMinus24Hours && currentTime <= deadline) {
      setShowAttachment(false)
    } else {
      setShowAttachment(true)
    }
  }, [auctionDetails])

  const attachmentSubmit = () => {
    setLoadingBtn(1)
    auctionDetailsStore
      .insertDocument(attachment)
      .then(() => {
        setAttachment({ ...attachment, file: null })
      })
      .finally(() => setLoadingBtn(0))
  }

  const solvency = (solvencyAttachments: any) => {
    let value = true
    solvencyAttachments.forEach((solvency: any) => {
      if (solvency.user.email === userStore.user?.email) {
        value = false
      }
    })
    return value
  }

  const handleDeleteAttachment = (id: string) => {
    setLoadingBtn(2)
    auctionDetailsStore.deleteDocument(id).finally(() => setLoadingBtn(0))
  }

  return (
    <>
      {/* Attachment file upload */}
      {auctionDetails?.attachment && (
        <>
          {solvency(auctionDetails?.solvencyAttachment) && (
            <div className='auction-details-solvency'>
              <label>{t('solvency_attachment')}</label>
              <InputGroup className='mb-3'>
                <Form.Control
                  type='file'
                  name='file'
                  accept='.jpeg, .jpg, .png, .webp, application/pdf'
                  onChange={(e: any) =>
                    setAttachment({
                      ...attachment,
                      [e.target.name]: e.currentTarget.files[0],
                    })
                  }
                />
                <Button
                  disabled={loadingBtn === 1 || !showAttachment || !userStore.isLoggedIn}
                  onClick={attachmentSubmit}
                  className='auction-details-solvency-submit-btn'
                >
                  {loadingBtn === 1 ? <Spinner animation='border' /> : t('submit')}
                </Button>
                <Tooltip title={<SolvencyRenderer deadline={auctionDetails!.deadline} />}>
                  <ErrorIcon fontSize='large' sx={{ color: '#ababab' }} className='ms-2 mt-1' />
                </Tooltip>
              </InputGroup>
            </div>
          )}

          {/* Attachment Desc after uploading */}
          {auctionDetails?.solvencyAttachment?.map((solvency) =>
            solvency.document.map(
              (document) =>
                solvency.user?.email === userStore.user?.email && (
                  <div key={document.id}>
                    <div className='d-flex flex-wrap'>
                      <p className='text-muted'>
                        <span
                          className={`solvency-approvement ${
                            solvency.valid === 0
                              ? 'bg-warning'
                              : solvency.valid === 1
                                ? 'bg-danger'
                                : 'bg-success'
                          }`}
                        >
                          {solvency.valid === 0
                            ? t('pending')
                            : solvency.valid === 1
                              ? t('declined')
                              : t('accepted')}
                        </span>
                      </p>
                      <p className='document-name'>
                        {t('document')}: <u>{document.name}</u>
                      </p>
                    </div>
                    <div className='solvency-btns'>
                      <button
                        disabled={loadingBtn === 2}
                        onClick={() => handleDeleteAttachment(solvency.id)}
                      >
                        {loadingBtn === 2 ? <Spinner animation='border' /> : t('del_doc')}
                      </button>
                      <a href={document.documentUrl} target='_blank' rel='noopener noreferrer'>
                        {t('open_doc')}
                      </a>
                    </div>
                    <AuctionCardLine />
                  </div>
                ),
            ),
          )}

          {/* Moderator / Auction Owner Solvency Review */}
          {methodsStore.canReviewAuction() && (
            <button
              onClick={() => setShowReview(true)}
              className='auction-details-solvency-review-btn'
            >
              {t('review_attachments')}
            </button>
          )}

          <Modal
            show={showReview}
            centered
            animation={false}
            size='lg'
            onHide={() => setShowReview(false)}
          >
            <AttachmentReviewModal setShow={setShowReview} />
          </Modal>
        </>
      )}
    </>
  )
}
export default observer(AuctionSolvencyAttachment)
